import * as React from 'react';
import 'twin.macro';
import { useTranslation } from 'next-i18next';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import tw from 'twin.macro';
import { useAtomValue } from 'jotai';
import { useMutation } from '@tanstack/react-query';

import { ButtonV2, OTPInput } from '@restworld/ui-ds';
import { useAccountsService } from '@restworld/data-services';

import { useCountDownTimerForOTP } from '../../user-settings/otp-validation-modal';
import { activeAddressIdAtom } from './google-address-input-result';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import { useOrganizationRestaurantOnboardingMutationQuery } from './restaurant-onboarding-mutation-query';
import { useFormatRestaurantOnboardingData } from './restaurant-onboarding-modal.hooks';

const useOTPVerificationMutation = (onOTPVerificationSuccess?: (formattedData: Record<string, unknown>) => void) => {
  const acc = useAccountsService();
  const { mutate: onRestaurantOnboardingFormSubmit, isPending: isOrganizationOnboarding } =
    useOrganizationRestaurantOnboardingMutationQuery();
  const formatFormData = useFormatRestaurantOnboardingData();
  const { getValues } = useFormContext<RestaurantOnboardingPayload>();
  const formattedData = formatFormData(getValues());

  const mutation = useMutation({
    mutationFn: async (params: { otpCode: string; gmapsPlaceId: string }) => (await acc.verifyPhoneOtp(params)).data,
    onSuccess(data) {
      // If custom callback provided (for org onboarding), use it instead
      if (onOTPVerificationSuccess) {
        onOTPVerificationSuccess(formattedData);
      } else {
        // Default restaurant onboarding flow
        onRestaurantOnboardingFormSubmit(formattedData);
      }
      return data;
    },
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error, `Error on otp verification!`);
      return error;
    },
  });
  return { mutation, isOrganizationOnboarding };
};

const useSendPhoneNumberOTP = () => {
  const acc = useAccountsService();
  return useMutation({
    mutationFn: async (data: { phoneNumber?: string; gmapsPlaceId: string; existingUserId?: string }) =>
      (await acc.sendVerificationPhoneOtp(data)).data,
    onSuccess(data) {
      return data;
    },
    onError(error) {
      return error;
    },
  });
};

interface IRestaurantOwnerOTPValidationProps {
  onOTPVerificationSuccess?: (formattedData: Record<string, unknown>) => void;
  /** When set, OTP is sent/resent using the existing user ID instead of the form phone number */
  existingUserId?: string;
  /** Masked phone (e.g. "***1234") to display instead of the entered phone number */
  maskedPhone?: string;
  /** When true, skip sending OTP on mount (already sent before rendering this component) */
  skipInitialSend?: boolean;
  /** Override gmapsPlaceId instead of reading from activeAddressIdAtom (used in org onboarding where the atom is cleared) */
  gmapsPlaceIdOverride?: string;
  /**
   * Loading state of the org-creation work triggered by `onOTPVerificationSuccess` in the
   * org-onboarding flow. The OTP verify endpoint is fast, but the follow-up creation call runs in
   * a separate mutation, so the button must keep spinning until that resolves too.
   */
  isCreatingOrganizationEntities?: boolean;
}
const RestaurantOwnerOTPValidation: React.FunctionComponent<IRestaurantOwnerOTPValidationProps> = ({
  onOTPVerificationSuccess,
  existingUserId,
  maskedPhone,
  skipInitialSend = false,
  gmapsPlaceIdOverride,
  isCreatingOrganizationEntities = false,
}) => {
  const { t } = useTranslation('restaurant-onboarding');
  const {
    mutation: {
      mutate: verifyPhoneOtp,
      isSuccess: isOTPVerificationSuccessful,
      isPending: isOTPVerificationLoading,
      isError: isOtpVerificationError,
    },
    isOrganizationOnboarding: isOrganizationOnboardingLoading,
  } = useOTPVerificationMutation(onOTPVerificationSuccess);
  const { mutate: sendPhoneNumberOTP, isPending: isSendOtpLoading } = useSendPhoneNumberOTP();
  const useCreateRestaurantOrgForm = useFormContext<RestaurantOnboardingPayload>();
  const phoneNumber = useCreateRestaurantOrgForm.getValues('phone_number');
  const displayPhone = maskedPhone || phoneNumber;
  const methods = useForm<{
    otpCode?: string;
  }>({
    defaultValues: {
      otpCode: '',
    },
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = methods;
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const gmapsPlaceId = gmapsPlaceIdOverride || activeAddressId;
  const { countDownLabel, resetTimer } = useCountDownTimerForOTP({ triggerCountdown: true });

  const onSubmit = handleSubmit(
    React.useCallback(
      (data) => {
        verifyPhoneOtp({ otpCode: data.otpCode.toString(), gmapsPlaceId });
      },
      [gmapsPlaceId, verifyPhoneOtp]
    )
  );

  const onOTPResend = React.useCallback(() => {
    if (existingUserId) {
      sendPhoneNumberOTP({ existingUserId, gmapsPlaceId });
    } else {
      sendPhoneNumberOTP({ phoneNumber, gmapsPlaceId });
    }
    resetTimer();
  }, [existingUserId, gmapsPlaceId, phoneNumber, resetTimer, sendPhoneNumberOTP]);

  React.useEffect(() => {
    if (!skipInitialSend) {
      if (existingUserId) {
        sendPhoneNumberOTP({ existingUserId, gmapsPlaceId });
      } else {
        sendPhoneNumberOTP({ phoneNumber, gmapsPlaceId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    reset({ otpCode: '' });
  }, [reset, isOTPVerificationSuccessful]);

  React.useEffect(() => {
    if (isOtpVerificationError) {
      methods.setValue('otpCode', '');
      methods.setError('otpCode', {
        message: t('validate_otp_insert_code_prompt'),
      });
    }
  }, [isOtpVerificationError, methods, t]);

  const otpCodeRealTime = watch('otpCode');
  return (
    <div tw="w-full flex flex-col items-center justify-center">
      <form
        onSubmit={onSubmit}
        action="#!"
        method="post"
        tw="self-stretch inline-flex flex-col justify-start items-center gap-8"
      >
        <div tw="self-stretch flex flex-col justify-start items-center gap-5">
          <div tw="self-stretch flex flex-col justify-start items-center gap-3">
            <h5 tw="self-stretch text-center justify-start text-neutral-700 text-xl font-semibold leading-[30px]">
              {t('verify_phone')}
            </h5>
            <p tw="self-stretch text-center text-neutral-500 text-base font-normal m-0">{t('verify_phone_subtitle')}</p>
          </div>
        </div>
        <h5 tw="self-stretch text-center justify-start text-neutral-200 text-base font-medium">
          {t('sms_sent_to', {
            phoneNumber: displayPhone,
          })}
        </h5>
        <div tw="self-stretch flex flex-col md:flex-row gap-1 text-center justify-center">
          <h5 tw="text-neutral-200 text-base font-medium">{t('ensure_real_manager')} </h5>
          <h5 tw="text-neutral-200 text-base font-bold">{t('real_manager')}</h5>
        </div>
        <div tw="flex flex-col gap-2 justify-start">
          <Controller
            name="otpCode"
            control={control}
            rules={{
              required: t('validate_otp_insert_code_prompt'),
              validate: (value) => value?.toString().length === 6,
            }}
            render={({ field }) => (
              <OTPInput
                variant={errors['otpCode'] ? 'error' : 'primary'}
                isSuccess={false}
                {...field}
                value={field?.value?.toString()}
                customTextFieldStyles={tw`md:(w-[90px] max-w-[95px] h-[48px]) bg-neutral-30 border border-neutral-90`}
              />
            )}
          />
          {errors['otpCode'] && <div tw="text-red-500 text-sm font-medium">{t('validate_otp_insert_code_prompt')}</div>}
        </div>
        <div tw="self-stretch inline-flex flex-col justify-start items-center gap-5 w-full">
          <ButtonV2
            disabled={otpCodeRealTime?.toString().length === 6 ? false : true}
            type="submit"
            onClick={onSubmit}
            twStyle={tw`w-full`}
            variant="primary"
            loading={isOTPVerificationLoading || isOrganizationOnboardingLoading || isCreatingOrganizationEntities}
          >
            {t('verify_number')}
          </ButtonV2>
          <ButtonV2
            disabled={!!countDownLabel}
            onClick={onOTPResend}
            twStyle={tw`w-full border-none shadow-none`}
            variant="tertiary"
            loading={isSendOtpLoading}
          >
            {t('resend_code')} {countDownLabel || ''}
          </ButtonV2>
        </div>
      </form>
    </div>
  );
};

export default RestaurantOwnerOTPValidation;
